import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';

import FormGroup from '../../../components/global/FormGroup';
import Button from '../../../components/global/Button';
import withModal from '../../../components/global/modal/withModal';
import { UPDATE_ACCOUNT_IDENTITY_MODAL } from '../constants/modals';
import IdentityAvatarContainer from '../../../containers/global/identity/IdentityAvatarContainer';
import { AVATAR_SIZE_72 } from '../../../constants/avatar-sizes';
import Spinner from '../../../components/global/Spinner';
import MissingFieldsAlert from '../../../components/global/MissingFieldsAlert';
import ContextualString from '../../../components/contextual/ContextualString';
import withVirtualPageview from '../../../components/global/hoc/withVirtualPageview';
import Label from '../../../components/global/Label';
import IdentityGenderSelectionButtons from './IdentityGenderSelectionButtons';
import { requiresGenderForOnboarding } from '../../../util/album-folder';
import Alert from '../../../components/global/Alert';
import UpdateSelfieFormModal, {
  UPDATE_SELFIE_FORM_MODAL,
} from '../../../components/global/UpdateSelfieForm/modal';
import WarningIcon from '../../../components/icons/WarningIcon';
import DismissCircleIcon from '../../../components/icons/DismissCircleIcon';
import DismissButton from '../../../components/global/DismissButton';
import useRequestConfirm from '../../../hooks/useRequestConfirm';
import { IDENTITY_RELATIONSHIP } from '../../../constants/identity';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  actionProps: PropTypes.shape({
    identity: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      last_name: PropTypes.string,
      gender: PropTypes.string,
      relationship: PropTypes.string,
      album_memberships: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            album: PropTypes.shape({
              id: PropTypes.string,
              root_album_folder: PropTypes.shape({
                id: PropTypes.string,
              }),
            }),
          }),
        ),
      }),
    }).isRequired,
    outdatedSelfie: PropTypes.bool,
    forceSelfieUpdate: PropTypes.bool,
    isPrimaryIdentity: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  }),
  identityUpdate: PropTypes.func.isRequired,
  identityUpdateReset: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  success: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  identityUpdateMutation: PropTypes.func.isRequired,
  updateSelfieMutation: PropTypes.func.isRequired,
  stringContext: PropTypes.shape({
    album_type: PropTypes.string,
    album_subtype: PropTypes.string,
  }),
  openModal: PropTypes.func,
  onIdentityUpdated: PropTypes.func,
  onIdentityDelete: PropTypes.func,
  formClassName: PropTypes.string,
};

const UpdateAccountIdentity = ({
  account,
  actionProps,
  identityUpdate,
  identityUpdateReset,
  loading,
  error,
  success,
  onRequestClose,
  identityUpdateMutation,
  updateSelfieMutation,
  stringContext,
  openModal,
  onIdentityUpdated,
  onIdentityDelete,
  formClassName,
}) => {
  const {
    identity,
    outdatedSelfie,
    forceSelfieUpdate,
    isPrimaryIdentity,
    title,
  } = actionProps;
  const formClasses = classNames({
    'px-md-4': true,
    [formClassName]: !!formClassName,
  });

  const [firstName, setFirstName] = useState(
    identity?.name && identity?.name !== 'Me' ? identity.name : '',
  );
  const [lastName, setLastName] = useState(identity?.last_name ?? '');
  const [gender, setGender] = useState(identity?.gender ?? undefined);
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [showMissingFields, setShowMissingFields] = useState(false);
  const requestConfirm = useRequestConfirm();
  const hasPhoto = !!identity?.current_photo;

  const isPrimaryIdentityBool =
    !!isPrimaryIdentity ||
    identity?.relationship === IDENTITY_RELATIONSHIP.PRIMARY;

  const IdentityReferenceLabel = () => (
    <ContextualString
      id="identityReference"
      defaultMessage="member"
      type={stringContext?.album_type}
      subtype={stringContext?.album_subtype}
      formatter={value => (value === 'member' ? 'family member' : value)}
    />
  );

  // The identity is only here to get a selfie: it already has both names and
  // the caller told us a photo is required. Jump straight to the selfie
  // capture instead of landing on (and focusing) the name form.
  const goStraightToSelfie =
    !!forceSelfieUpdate && !hasPhoto && !!firstName && !!lastName;

  useEffect(() => {
    identityUpdateReset();

    if (goStraightToSelfie) {
      openModal?.(UPDATE_SELFIE_FORM_MODAL, { identity });
    }
  }, []);

  useEffect(() => {
    if (success) {
      onRequestClose();

      // Reset the success value before closing the modal. Otherwise,
      // this effect will run with SUCCESS when this modal is opened again.
      identityUpdateReset();
      if (onIdentityUpdated && identity) {
        onIdentityUpdated({
          ...identity,
          name: firstName,
          last_name: lastName,
          gender,
        });
      }
    }
  }, [success]);

  const isFormValid = () => {
    if (requiresGenderForOnboarding(stringContext) && !gender) {
      return false;
    }

    if ((forceSelfieUpdate || outdatedSelfie) && !file) {
      return false;
    }

    return !!firstName && !!lastName;
  };

  const onUpdateIdentity = async event => {
    if (event) {
      event.preventDefault();
    }

    if (!isFormValid()) {
      setShowMissingFields(true);
      return;
    }

    await identityUpdate({
      identity,
      identityFirstName: firstName,
      identityLastName: lastName,
      gender,
      file,
      identityUpdateMutation,
      updateSelfieMutation,
    });
  };

  useEffect(() => {
    if (file && firstName && lastName) {
      onUpdateIdentity();
    }
  }, [file]);

  const requiresGender = requiresGenderForOnboarding(stringContext);

  const requiredFields = [
    {
      value: firstName,
      name: 'First Name',
    },
    {
      value: lastName,
      name: 'Last Name',
    },
  ];

  if (requiresGender) {
    requiredFields.push({ value: gender, name: 'Gender' });
  }

  if ((forceSelfieUpdate || outdatedSelfie) && !file) {
    requiredFields.push({ value: file, name: 'Updated Photo' });
  }

  const hasUnsavedChanges = () => {
    if (!identity) {
      return false;
    }

    return (
      identity.name !== firstName ||
      identity.last_name !== lastName ||
      (!!identity.gender && identity.gender !== gender) ||
      //  If file is true, something has been uploaded.
      !!file
    );
  };

  return (
    <Fragment>
      <div className="w-100 d-flex justify-content-end mb-2">
        <DismissButton
          data-testid="dismiss-btn"
          onClick={() => {
            if (hasUnsavedChanges()) {
              requestConfirm({
                message: (
                  <p className="px-2 text-center">
                    You have unsaved changes.
                    <br />
                    <br />
                    Do you want to save before leaving?
                  </p>
                ),
                onConfirm: event => onUpdateIdentity(event),
                confirmText: 'Save',
                cancelText: "Don't save",
                onCancel: () => onRequestClose(),
              });
            } else {
              onRequestClose();
            }
          }}
        />
      </div>
      <form className={formClasses}>
        <h3 className="text-center mb-2">
          {title ? (
            <>{title}</>
          ) : (
            <>
              Update{' '}
              {isPrimaryIdentityBool ? (
                <Fragment>your information</Fragment>
              ) : (
                <IdentityReferenceLabel />
              )}
            </>
          )}
        </h3>
        <FormGroup
          id="identity-first-name"
          name="firstName"
          type="text"
          label={
            <Fragment>
              {isPrimaryIdentityBool ? (
                <Fragment>Your</Fragment>
              ) : (
                <Fragment>
                  <IdentityReferenceLabel />
                  &apos;s
                </Fragment>
              )}{' '}
              First Name
            </Fragment>
          }
          required
          autoFocus={!goStraightToSelfie}
          value={firstName}
          onChange={({ target }) => setFirstName(target.value)}
        />

        <FormGroup
          id="identity-last-name"
          name="lastName"
          type="text"
          label={
            <Fragment>
              {isPrimaryIdentityBool ? (
                <Fragment>Your</Fragment>
              ) : (
                <Fragment>
                  <IdentityReferenceLabel />
                  &apos;s
                </Fragment>
              )}{' '}
              Last Name
            </Fragment>
          }
          required
          value={lastName}
          onChange={({ target }) => setLastName(target.value)}
        />

        {requiresGender && (
          <Fragment>
            <Label
              id="identity-gender"
              labelNote="(required)"
              label={
                <Fragment>
                  {isPrimaryIdentityBool ? (
                    <Fragment>Your</Fragment>
                  ) : (
                    <Fragment>
                      <IdentityReferenceLabel />
                      &apos;s
                    </Fragment>
                  )}{' '}
                  Gender
                </Fragment>
              }
            />
            <IdentityGenderSelectionButtons
              value={gender}
              onChange={value => setGender(value)}
              className="mb-2"
            />
          </Fragment>
        )}

        <div className="d-flex justify-content-center align-items-center mb-4">
          <IdentityAvatarContainer
            identity={fileUrl ? undefined : identity}
            uploadedPhoto={
              fileUrl
                ? {
                    url: fileUrl,
                  }
                : undefined
            }
            size={AVATAR_SIZE_72}
            onClick={event => event.preventDefault()}
            className="identity-selfie-button"
            data-for="outdated-selfie-warning-tooltip"
            data-place="top"
            data-effect="solid"
            data-tip
          >
            {outdatedSelfie && !file && (
              <div className="outdated-selfie-warning">
                <WarningIcon color="white" size={32} />
              </div>
            )}
          </IdentityAvatarContainer>
          {outdatedSelfie && !file && (
            <ReactTooltip
              id="outdated-selfie-warning-tooltip"
              className="tooltip"
            >
              <span>
                Your selfie hasn&apos;t been updated for some time, and this
                could affect your photo matching. <br />
                <br />
                Click below to update your selfie now!
              </span>
            </ReactTooltip>
          )}
          <Button
            id="update-selfie-button"
            btnBlock
            btnOutline
            className="ml-3"
            onClick={event => {
              event.preventDefault();
              openModal(UPDATE_SELFIE_FORM_MODAL, { identity });
            }}
          >
            {hasPhoto ? 'Update' : 'Add'} Photo
          </Button>
        </div>

        {loading && (
          <div className="text-center">
            <Spinner />
          </div>
        )}

        {!loading && (
          <Fragment>
            {showMissingFields && (
              <MissingFieldsAlert
                customMessage={
                  <p className="text-bold">
                    Don&apos;t forget to fill all required fields! Please
                    provide:
                  </p>
                }
                type="danger"
                fields={requiredFields}
              />
            )}

            {error && <Alert type="danger">{error}</Alert>}
            <Button
              id="update-account-identity-btn"
              btnBlock
              className="mb-3"
              onClick={event => onUpdateIdentity(event)}
            >
              Save
            </Button>
          </Fragment>
        )}
        {onIdentityDelete && !isPrimaryIdentityBool && (
          <Fragment>
            <hr />
            <div className="d-flex justify-content-center">
              <Button
                btnStyle="reset"
                className="d-flex align-items-center text-sm text-danger text-bold mb-2"
                onClick={() => onIdentityDelete(identity)}
              >
                <DismissCircleIcon color="#fe5851" className="mr-1" /> Remove{' '}
                {firstName} from account
              </Button>
            </div>
            <p className="text-center text-sm">
              Once removed, you will{' '}
              <span className="text-danger">lose all paid subscriptions</span>{' '}
              associated with this profile!
            </p>
          </Fragment>
        )}
        <UpdateSelfieFormModal
          uploadOnly
          user={account}
          onSuccess={uploadedFile => {
            setFile(uploadedFile);
            setFileUrl(uploadedFile.url);
          }}
        />
      </form>
    </Fragment>
  );
};

UpdateAccountIdentity.propTypes = propTypes;

export { UpdateAccountIdentity };

export default withModal(
  withVirtualPageview({
    path: 'update-identity',
    title: 'Update Identity',
  })(UpdateAccountIdentity),
  {
    name: UPDATE_ACCOUNT_IDENTITY_MODAL,
    size: 'sm',
    type: 'full',
    hideHeader: true,
    dismissible: false,
    closeOnOverlayClick: false,
  },
);
